import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { Settings, APP_CONFIG } from 'src/app/config/settings';
import { combine, arrayToQuery, appendQuery } from 'src/app/shared/utilities/route';
import { CandideGridItem } from 'src/app/travelxnew/models/candide-grid-item.model';
import { CandidateGridLookups, Dictionary } from 'src/app/travelxnew/models/candidate-grid-lookups.model';
import { UpdateWorkApproval } from 'src/app/travelxnew/models/update-work-approval.model';
import { Recruiter } from 'src/app/travelxnew/models/recruiter.model';
import { CorporateDirectoryUser } from 'src/app/corporate-directory/models/corporate-directory-user.model';
import { AssignTravelXTeamByRecruiter } from 'src/app/travelxnew/models/assign-travelx-team-by-recruiter.model';
import { AssignTravelXTeamByCandidate } from 'src/app/travelxnew/models/assign-travelx-team-by-candidate.model';

@Injectable({
    providedIn: 'root'
})
export class TravelXNewService {
    private readonly coreApiPath: string;

    constructor(private readonly http: HttpClient, @Inject(APP_CONFIG) settings: Settings) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    getContracts(args: FetchArgs): Observable<PagingToken<CandideGridItem[]>> {
        let url = `${this.coreApiPath}/contracts`;
        const { pageSize, skip } = args;
        if (args.filter) {
            for (const prop in args.filter) {
                const val = args.filter[prop];
                if (Array.isArray(val)) {
                    url = appendQuery(url, arrayToQuery(prop, val));
                } else if (val) {
                    url = appendQuery(url, `${prop}=${val}`);
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<CandideGridItem[]>>(path);
    }

    getLookups(includeAllCancellationReasons: boolean = false): Observable<CandidateGridLookups> {
        return this.http.get<CandidateGridLookups>(
            `${this.coreApiPath}/contracts/lookups?includeAllCancellationReasons=${includeAllCancellationReasons}`
        );
    }

    getStates() {
        return this.http.get(`${this.coreApiPath}/states/`);
    }

    getContractsCount(): Observable<Dictionary<string>> {
        return this.http.get<Dictionary<string>>(`${this.coreApiPath}/contracts/counts`);
    }

    getContractsCountByStage(stage: 'PRE' | 'WOR' | 'PDP' | 'PDW' | 'CAN' | 'COM' | 'REJ' | 'PEN'): Observable<number> {
        return this.http.get<number>(`${this.coreApiPath}/contracts/counts/${stage}`);
    }

    getContractById(contractId): Observable<any> {
        return this.http.get<number>(`${this.coreApiPath}/contracts/id/${contractId}`);
    }

    getRecruiters(employeeName: string | null): Observable<Recruiter[]> {
        const url = `${this.coreApiPath}/travelx/recruiters`;
        if (employeeName) {
            return this.http.get<Recruiter[]>(url, { params: { employeeName } });
        } else {
            return this.http.get<Recruiter[]>(url);
        }
    }

    getTravelXPersonnel(employeeName: string | null): Observable<CorporateDirectoryUser[]> {
        const url = `${this.coreApiPath}/travelx/personnel`;
        if (employeeName) {
            return this.http.get<CorporateDirectoryUser[]>(url, { params: { employeeName } });
        } else {
            return this.http.get<CorporateDirectoryUser[]>(url);
        }
    }

    updateContractStageCode(data: any): Observable<any> {
        return this.http.put(`${this.coreApiPath}/contracts`, data);
    }

    updateWorkApproval(data: UpdateWorkApproval): Observable<any> {
        return this.http.post(`${this.coreApiPath}/contracts/updateWorkApproval`, data);
    }

    removeCancelation(id: any): Observable<any> {
        return this.http.delete(`${this.coreApiPath}/contracts/${id}/cancellation`);
    }

    assignByRecruiter(data: AssignTravelXTeamByRecruiter): Observable<void> {
        return this.http.post<void>(`${this.coreApiPath}/travelx/assign-by-recruiter`, data);
    }

    assignByCandidate(data: AssignTravelXTeamByCandidate): Observable<void> {
        return this.http.post<void>(`${this.coreApiPath}/travelx/assign-by-candidate`, data);
    }
}
